import { useQuery } from "@tanstack/react-query";
import { useDataProvider } from "ra-core";

import type { CrmDataProvider } from "../providers/types";
import type { ContactCohortFilter, ContactFilterFacets } from "../types";
import { formatLanguage, languageLabel } from "../contact-language/languages";
import { continentLabel, parseCodeFilter } from "./geography";

const DAY_MS = 24 * 3600000;

// Equality buckets that the contact list surfaces as chips and that the backend
// resolver pushes down to the store. `messaging_window` / `reachable_only` are
// intentionally absent: they are live-computed and not list-filterable, so they
// are refined inside the broadcast composer instead.
const COHORT_EQUALITY_KEYS = [
  "lifecycle_stage",
  "video_count_bucket",
  "use_count_bucket",
  "average_rating_bucket",
  "rating_bucket",
  "whatsapp_opt_in_status",
  "acquisition_channel",
  // Bot-side preferred language (LANG-401 `CONTACT_LIST_POST_FILTER_FIELDS`).
  "preferred_language",
] as const;

const useDashboardCapabilities = () => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  return useQuery({
    queryKey: ["dashboardCapabilities"],
    queryFn: () => dataProvider.getDashboardCapabilities(),
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * True when the active dashboard backend advertises broadcast cohort support.
 */
export const useCohortFiltersEnabled = (): boolean => {
  const { data } = useDashboardCapabilities();
  return data?.features?.broadcast_cohort_filters === true;
};

export const useContactEchoMetricsEnabled = (): boolean => {
  const { data } = useDashboardCapabilities();
  return (
    data?.features?.contact_echo_metrics === true ||
    data?.features?.broadcast_cohort_filters === true
  );
};

/**
 * True when the active backend can report per-bucket population counts for the
 * contact filter chips (the `/contacts/facets` endpoint).
 */
export const useContactFilterFacetsEnabled = (): boolean => {
  const { data } = useDashboardCapabilities();
  return data?.features?.contact_filter_facets === true;
};

/**
 * Fetches the per-bucket contact counts that drive the "(n)" badges on the
 * sidebar filter chips. No-op (disabled) unless the backend advertises facets.
 */
export const useContactFilterFacets = () => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const enabled = useContactFilterFacetsEnabled();
  return useQuery({
    queryKey: ["contactFilterFacets"],
    queryFn: () => dataProvider.getContactFilterFacets(),
    enabled,
    staleTime: 60 * 1000,
  });
};

/**
 * Count of contacts in `facets[field][value]`, or undefined when the facet map
 * has not loaded / does not cover this bucket. Pure, so it is unit-testable.
 */
export const facetCount = (
  facets: ContactFilterFacets["facets"] | undefined,
  field: string,
  value: string,
): number | undefined => facets?.[field]?.[value];

export const useContactGeographyEnabled = (): boolean => {
  const { data } = useDashboardCapabilities();
  return data?.features?.contact_phone_geography === true;
};

/**
 * Maps the active contact-list filters into a broadcast `contact_filter`.
 * Equality buckets pass through unchanged; the existing `last_seen@gte/lte`
 * recency chips become `active_within_days` / `dormant_beyond_days` (mirroring
 * the resolver's gsi1sk-ordered recency). Returns null when no cohort dimension
 * is active so callers can hide the segment-broadcast affordance.
 */
export const extractCohortFilter = (
  filterValues: Record<string, unknown> | undefined,
  now: number = Date.now(),
): ContactCohortFilter | null => {
  if (!filterValues) return null;

  const filter: ContactCohortFilter = {};
  let hasCohort = false;

  for (const key of COHORT_EQUALITY_KEYS) {
    const value = filterValues[key];
    if (value === undefined || value === null || value === "") continue;
    (filter as Record<string, unknown>)[key] = value;
    hasCohort = true;
  }

  const continentIn = parseCodeFilter(filterValues["phone_continent_code@in"]);
  if (continentIn.length) {
    filter.geo_continents_any = continentIn;
    hasCohort = true;
  }
  const exactContinent = filterValues.phone_continent_code;
  if (typeof exactContinent === "string" && exactContinent) {
    filter.geo_continents_any = [exactContinent];
    hasCohort = true;
  }

  const countryIn = parseCodeFilter(filterValues["phone_country_iso2@in"]);
  if (countryIn.length) {
    filter.geo_countries_any = countryIn;
    hasCohort = true;
  }

  const gte = filterValues["last_echo_at@gte"] ?? filterValues["last_seen@gte"];
  if (typeof gte === "string" && gte) {
    const days = Math.ceil((now - Date.parse(gte)) / DAY_MS);
    if (Number.isFinite(days)) {
      filter.active_within_days = Math.max(1, days);
      hasCohort = true;
    }
  }

  const lte = filterValues["last_echo_at@lte"] ?? filterValues["last_seen@lte"];
  if (typeof lte === "string" && lte) {
    const days = Math.floor((now - Date.parse(lte)) / DAY_MS);
    if (Number.isFinite(days)) {
      filter.dormant_beyond_days = Math.max(0, days);
      hasCohort = true;
    }
  }

  return hasCohort ? filter : null;
};

const COHORT_LABEL_PREFIX = "crm.whatsapp.broadcasts.cohort.";

export type CohortSummaryChip = {
  key: string;
  options?: Record<string, number | string>;
};

/**
 * Translation keys (plus interpolation options) describing each active dimension
 * of a cohort filter, for a human-readable summary in the composer. Pure so it
 * is unit-testable.
 */
export const cohortFilterLabelKeys = (
  filter: ContactCohortFilter,
): CohortSummaryChip[] => {
  const chips: CohortSummaryChip[] = [];
  if (filter.lifecycle_stage) {
    chips.push({
      key: `${COHORT_LABEL_PREFIX}lifecycle_${filter.lifecycle_stage}`,
    });
  }
  if (filter.video_count_bucket) {
    chips.push({
      key: `${COHORT_LABEL_PREFIX}videos_${filter.video_count_bucket}`,
    });
  }
  if (filter.use_count_bucket) {
    chips.push({
      key: `${COHORT_LABEL_PREFIX}uses_${filter.use_count_bucket}`,
    });
  }
  if (filter.average_rating_bucket) {
    chips.push({
      key: `${COHORT_LABEL_PREFIX}average_rating_${filter.average_rating_bucket}`,
    });
  }
  if (filter.rating_bucket && filter.rating_bucket !== "none") {
    chips.push({ key: `${COHORT_LABEL_PREFIX}rating_${filter.rating_bucket}` });
  }
  if (filter.last_rating_any?.length) {
    chips.push({
      key: `${COHORT_LABEL_PREFIX}rating_stars`,
      options: {
        stars: [...filter.last_rating_any].sort((a, b) => a - b).join(", "),
      },
    });
  }
  if (filter.last_rating_none) {
    chips.push({ key: `${COHORT_LABEL_PREFIX}rating_unrated` });
  }
  if (filter.whatsapp_opt_in_status === "opted_in") {
    chips.push({ key: `${COHORT_LABEL_PREFIX}opted_in` });
  }
  if (filter.acquisition_channel) {
    chips.push({
      key: `${COHORT_LABEL_PREFIX}acquisition_${filter.acquisition_channel}`,
    });
  }
  if (filter.preferred_language) {
    chips.push({
      key: `${COHORT_LABEL_PREFIX}language_any`,
      options: {
        language: formatLanguage(languageLabel(filter.preferred_language)),
      },
    });
  }
  if (filter.active_within_days != null) {
    chips.push({
      key: `${COHORT_LABEL_PREFIX}active_within_days`,
      options: { days: filter.active_within_days },
    });
  }
  if (filter.dormant_beyond_days != null) {
    chips.push({
      key: `${COHORT_LABEL_PREFIX}dormant_beyond_days`,
      options: { days: filter.dormant_beyond_days },
    });
  }
  if (filter.reachable_only) {
    chips.push({ key: `${COHORT_LABEL_PREFIX}reachable` });
  }
  if (filter.messaging_window === "open") {
    chips.push({ key: `${COHORT_LABEL_PREFIX}window_open` });
  }
  if (filter.messaging_window === "closed") {
    chips.push({ key: `${COHORT_LABEL_PREFIX}window_closed` });
  }
  if (filter.geo_continents_any?.length) {
    chips.push({
      key: `${COHORT_LABEL_PREFIX}geo_continents_any`,
      options: {
        continents: filter.geo_continents_any.map(continentLabel).join(", "),
      },
    });
  }
  if (filter.geo_continents_none?.length) {
    chips.push({
      key: `${COHORT_LABEL_PREFIX}geo_continents_none`,
      options: {
        continents: filter.geo_continents_none.map(continentLabel).join(", "),
      },
    });
  }
  if (filter.geo_countries_any?.length) {
    chips.push({
      key: `${COHORT_LABEL_PREFIX}geo_countries_any`,
      options: { countries: filter.geo_countries_any.join(", ") },
    });
  }
  if (filter.geo_countries_none?.length) {
    chips.push({
      key: `${COHORT_LABEL_PREFIX}geo_countries_none`,
      options: { countries: filter.geo_countries_none.join(", ") },
    });
  }
  return chips;
};
